import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Activity, Home, Mail, FileText, Shield, CheckCircle, XCircle, Clock, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';

const FollowUpStatsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dentalToken');
      
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/practice/activity-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStats(data.data);
        } else {
          setError('Failed to load statistics');
        }
      } else {
        const errorText = await response.text();
        console.error('Stats error:', response.status, errorText);
        setError(`Failed to load data: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Error loading stats:', error);
      setError('Network error loading statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Activity className="h-12 w-12 text-blue-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading statistics...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <Button 
              onClick={() => navigate('/dashboard')}
              variant="default"
              size="lg"
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3"
            >
              <Home className="h-5 w-5" />
              <span>Return to Dashboard</span>
            </Button>
          </div>
          
          <Card>
            <CardContent className="p-8">
              <div className="text-center">
                <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600 mb-4">{error}</p>
                <Button onClick={loadStats}>
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* PROMINENT Header with Return Button */}
        <div className="mb-8 bg-white p-6 rounded-lg shadow-lg border-2 border-blue-500">
          <div className="flex items-center justify-between">
            <Button 
              onClick={() => navigate('/dashboard')}
              variant="default"
              size="lg"
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 text-lg"
            >
              <Home className="h-6 w-6" />
              <span>Return to Dashboard</span>
            </Button>
            
            <Button onClick={loadStats} variant="outline" size="lg" className="px-6 py-4">
              <TrendingUp className="h-5 w-5 mr-2" />
              Refresh Data
            </Button>
          </div>
          
          <div className="mt-4">
            <h1 className="text-4xl font-bold text-gray-900 flex items-center">
              <Activity className="h-10 w-10 text-blue-600 mr-4" />
              Practice Activity Statistics
            </h1>
            <p className="text-gray-600 mt-2 text-lg">
              Overview of patient communications sent today
            </p>
          </div>
        </div>

        {stats && (
          <div className="space-y-6">
            {/* Procedure Instructions Sent */}
            <Card className="border-2 border-green-200">
              <CardHeader className="bg-green-50">
                <CardTitle className="flex items-center text-2xl">
                  <FileText className="h-8 w-8 mr-3 text-green-600" />
                  Procedure Instructions Sent
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-lg border-2 border-green-300 shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Emailed to Patients</p>
                        <p className="text-5xl font-bold text-green-600">{stats.procedures?.emailed || 0}</p>
                      </div>
                      <Mail className="h-16 w-16 text-green-400" />
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg border-2 border-blue-300 shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Total Delivered</p>
                        <p className="text-5xl font-bold text-blue-600">{stats.procedures?.total || 0}</p>
                      </div>
                      <FileText className="h-16 w-16 text-blue-400" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Informed Consents Sent */}
            <Card className="border-2 border-indigo-200">
              <CardHeader className="bg-indigo-50">
                <CardTitle className="flex items-center text-2xl">
                  <Shield className="h-8 w-8 mr-3 text-indigo-600" />
                  Informed Consents Sent
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-lg border-2 border-indigo-300 shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Emailed to Patients</p>
                        <p className="text-5xl font-bold text-indigo-600">{stats.consents?.emailed || 0}</p>
                      </div>
                      <Mail className="h-16 w-16 text-indigo-400" />
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg border-2 border-blue-300 shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Total Delivered</p>
                        <p className="text-5xl font-bold text-blue-600">{stats.consents?.total || 0}</p>
                      </div>
                      <Shield className="h-16 w-16 text-blue-400" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Follow-up Email Status */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-blue-50">
                <CardTitle className="flex items-center text-2xl">
                  <Mail className="h-8 w-8 mr-3 text-blue-600" />
                  Follow-up Email Status
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white p-6 rounded-lg border-2 border-yellow-300 shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Emails Scheduled</p>
                        <p className="text-5xl font-bold text-yellow-600">{stats.followups?.pending || 0}</p>
                        <p className="text-xs text-gray-500 mt-1">Waiting to be sent</p>
                      </div>
                      <Clock className="h-16 w-16 text-yellow-400" />
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg border-2 border-green-300 shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Emails Sent Successfully</p>
                        <p className="text-5xl font-bold text-green-600">{stats.followups?.sent || 0}</p>
                        <p className="text-xs text-gray-500 mt-1">Delivered to patients</p>
                      </div>
                      <CheckCircle className="h-16 w-16 text-green-400" />
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg border-2 border-red-300 shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Emails Failed</p>
                        <p className="text-5xl font-bold text-red-600">{stats.followups?.failed || 0}</p>
                        <p className="text-xs text-gray-500 mt-1">Delivery errors</p>
                      </div>
                      <XCircle className="h-16 w-16 text-red-400" />
                    </div>
                  </div>
                </div>
                
                {(stats.followups?.sent > 0 || stats.followups?.failed > 0) && (
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-2">Success Rate</p>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div 
                        className="bg-green-500 h-4 rounded-full transition-all flex items-center justify-center text-xs text-white font-bold" 
                        style={{ width: `${stats.followups?.success_rate || 0}%` }}
                      >
                        {stats.followups?.success_rate || 0}%
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Activity */}
            {stats.recent_activity && stats.recent_activity.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <Clock className="h-6 w-6 mr-2 text-gray-600" />
                    Recent Activity Log
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {stats.recent_activity.map((activity, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${activity.status === 'success' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {activity.category} - {activity.activityType}
                            </p>
                            <p className="text-xs text-gray-600">
                              {activity.patientName || 'Unknown'} ({activity.patientEmail || 'No email'})
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">{formatDate(activity.timestamp)}</p>
                          <span className={`text-xs px-2 py-1 rounded ${
                            activity.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {activity.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Bottom Return Button */}
        <div className="mt-8 text-center bg-white p-6 rounded-lg shadow-lg border-2 border-blue-500">
          <Button 
            onClick={() => navigate('/dashboard')}
            variant="default"
            size="lg"
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 text-lg mx-auto"
          >
            <Home className="h-6 w-6" />
            <span>Return to Dashboard</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FollowUpStatsPage;
